import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Spin, message } from "antd";
import { TagOutlined, CheckCircleOutlined, ShareAltOutlined } from "@ant-design/icons";
import CouponServ from "../../../service/CouponServ";
import moment from "moment";

const { Text } = Typography;

const CouponDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        totalCoupons: 0,
        liveCoupons: 0,
        totalRedemptions: 0,
    });

    const isCouponLive = (record) => {
        if (!record.isActive) return false;
        const now = moment();
        const start = moment(record.startDate);
        const end = moment(record.endDate);
        return now.isSameOrAfter(start, 'day') && now.isSameOrBefore(end, 'day');
    };

    const fetchAggregateStats = async () => {
        try {
            setLoading(true);
            // Fetching a large subset for dashboard stats aggregation.
            // Ideally this should be a dedicated backend endpoint, but for now
            // we aggregate client-side to avoid backend changes.
            const res = await CouponServ.index({ limit: 500 });
            const coupons = Array.isArray(res?.data) ? res.data : [];

            const liveCoupons = coupons.filter(c => isCouponLive(c)).length;
            const totalRedemptions = coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0);

            setStats({
                totalCoupons: res?.total || coupons.length,
                liveCoupons,
                totalRedemptions,
            });
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
            message.error("Failed to load dashboard statistics.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAggregateStats();
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" tip="Aggregating coupon data..." />
            </div>
        );
    }

    return (
        <div>
            {/* Summary Stats Cards */}
            <Row gutter={[16, 16]} className="pcu-margin-bottom-24">
                <Col xs={24} sm={8}>
                    <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                    }}>
                        <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.15 }}>
                            <TagOutlined style={{ fontSize: '90px' }} />
                        </div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                                    <TagOutlined style={{ fontSize: '16px' }} />
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.5px' }}>TOTAL COUPONS</span>
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: 900, lineHeight: 1, marginTop: '12px' }}>
                                {stats.totalCoupons}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={8}>
                    <div style={{
                        background: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(13, 148, 136, 0.2)'
                    }}>
                        <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.15 }}>
                            <CheckCircleOutlined style={{ fontSize: '90px' }} />
                        </div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                                    <CheckCircleOutlined style={{ fontSize: '16px' }} />
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.5px' }}>LIVE PROMOTIONS</span>
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: 900, lineHeight: 1, marginTop: '12px' }}>
                                {stats.liveCoupons}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={8}>
                    <div style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(234, 88, 12, 0.2)'
                    }}>
                        <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.15 }}>
                            <ShareAltOutlined style={{ fontSize: '90px' }} />
                        </div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                                    <ShareAltOutlined style={{ fontSize: '16px' }} />
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.5px' }}>TOTAL REDEMPTIONS</span>
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: 900, lineHeight: 1, marginTop: '12px' }}>
                                {stats.totalRedemptions}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

        </div>
    );
};

export default CouponDashboard;
